/**
 * Terms of Service dialog component.
 *
 * Terms displayed in a dialog overlay.
 */

import { APP_NAME, DEVELOPER_NAME } from '@idle-depths/shared';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

interface TermsDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog is closed */
  onClose: () => void;
}

/**
 * Terms of Service dialog.
 */
export function TermsDialog({ open, onClose }: TermsDialogProps): React.ReactElement {
  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent className="max-w-2xl max-h-[85vh] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-xl">Terms of Service</DialogTitle>
          <DialogDescription>Last updated: January 9, 2026</DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[calc(85vh-120px)] px-6 pb-6">
          <div className="space-y-6 text-sm text-foreground/90">
            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">1. Acceptance of Terms</h2>
              <p className="leading-relaxed">
                By accessing or using {APP_NAME} (the &quot;Game&quot;), you agree to be bound by
                these Terms of Service (&quot;Terms&quot;). If you do not agree to these Terms, you
                may not use the Game.
              </p>
              <p className="leading-relaxed">
                The Game is operated by {DEVELOPER_NAME} (&quot;we&quot;, &quot;our&quot;,
                &quot;us&quot;).
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">2. Eligibility</h2>
              <p className="leading-relaxed">
                You must be at least 13 years old to use the Game. By using the Game, you represent
                and warrant that you meet this age requirement.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">3. Account Registration</h2>
              <p className="leading-relaxed">
                To access certain features of the Game, you may create an account using Google
                Sign-In. You are responsible for:
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                <li>Maintaining the security of your account</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use</li>
              </ul>
              <p className="leading-relaxed">
                You may also play as a guest without creating an account, but guest progress is
                stored locally and will not sync across devices.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">4. User Conduct</h2>
              <p className="leading-relaxed">You agree not to:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                <li>Use the Game for any unlawful purpose</li>
                <li>Attempt to exploit, hack, or reverse engineer the Game</li>
                <li>Use automated tools, bots, or cheats</li>
                <li>Interfere with other users&apos; enjoyment of the Game</li>
                <li>Impersonate any person or entity</li>
                <li>Sell, trade, or transfer your account</li>
              </ul>
              <p className="leading-relaxed">
                Violation of these rules may result in account suspension or termination.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">
                5. Purchases and Payments
              </h2>
              <p className="leading-relaxed">
                The Game offers optional one-time purchases (&quot;DLCs&quot;) that unlock
                additional content and features. All purchases are:
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                <li>Final and non-refundable (except as required by law)</li>
                <li>Tied to your account</li>
                <li>Non-transferable</li>
              </ul>
              <p className="leading-relaxed">
                We reserve the right to modify pricing at any time. Price changes will not affect
                purchases already made.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">
                6. Intellectual Property
              </h2>
              <p className="leading-relaxed">
                All content in the Game, including but not limited to graphics, music, text, and
                code, is owned by {DEVELOPER_NAME} and is protected by copyright and other
                intellectual property laws.
              </p>
              <p className="leading-relaxed">
                You are granted a limited, non-exclusive, non-transferable license to use the Game
                for personal, non-commercial purposes.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">7. User Content</h2>
              <p className="leading-relaxed">
                If the Game allows you to create or share content, you retain ownership of your
                content but grant us a license to use it within the Game. You are solely responsible
                for any content you create or share.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">
                8. Disclaimer of Warranties
              </h2>
              <p className="leading-relaxed text-xs uppercase tracking-wide text-muted-foreground">
                The Game is provided &quot;as is&quot; and &quot;as available&quot; without
                warranties of any kind, express or implied. We do not warrant that the Game will be
                uninterrupted, error-free, or secure.
              </p>
              <p className="leading-relaxed text-xs uppercase tracking-wide text-muted-foreground">
                To the maximum extent permitted by law, we disclaim all warranties, including
                implied warranties of merchantability, fitness for a particular purpose, and
                non-infringement.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">
                9. Limitation of Liability
              </h2>
              <p className="leading-relaxed text-xs uppercase tracking-wide text-muted-foreground">
                To the maximum extent permitted by law, {DEVELOPER_NAME} shall not be liable for any
                indirect, incidental, special, consequential, or punitive damages, or any loss of
                profits or revenues, whether incurred directly or indirectly.
              </p>
              <p className="leading-relaxed text-xs uppercase tracking-wide text-muted-foreground">
                Our total liability shall not exceed the amount you paid to us, if any, in the
                twelve (12) months preceding the claim.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">
                10. Modifications to the Game
              </h2>
              <p className="leading-relaxed">
                We reserve the right to modify, suspend, or discontinue the Game at any time, with
                or without notice. We are not liable for any modification, suspension, or
                discontinuation of the Game.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">11. Changes to Terms</h2>
              <p className="leading-relaxed">
                We may update these Terms from time to time. We will notify you of material changes
                by posting the updated Terms and changing the &quot;Last updated&quot; date. Your
                continued use of the Game after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">12. Termination</h2>
              <p className="leading-relaxed">
                You may terminate your account at any time through the Account Settings page. We may
                terminate or suspend your account for any reason, including violation of these
                Terms.
              </p>
              <p className="leading-relaxed">
                Upon termination, your right to use the Game ceases immediately.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">13. Governing Law</h2>
              <p className="leading-relaxed">
                These Terms are governed by the laws of the United States, without regard to
                conflict of law principles. Any disputes shall be resolved in the courts of the
                applicable jurisdiction.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">14. Contact Us</h2>
              <p className="leading-relaxed">
                If you have any questions about these Terms, please contact us at:
              </p>
              <p>
                Email:{' '}
                <a
                  href="mailto:support@fireknotstudios.com"
                  className="text-biolume-cyan hover:underline"
                >
                  support@fireknotstudios.com
                </a>
              </p>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
